import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { connect, DispatchProp, useSelector } from 'react-redux';
import { isEmpty, isNil } from 'lodash';
import { CSpinner } from '@coreui/react';
import { useLocation, useHistory } from 'react-router-dom';
import { selectUserProfile } from '../store/selectors/profile.selectors';
import { LoadUserProfileInfo } from '../profile/actions/profile.action';
import { RootState } from '../store/state';
import { UserInfo } from '../shared/models/user';
import Toaster from '../shared/containers/Toaster';
import AppRouting from '../routes/AppRouting';
import { LoginRequest, WatchExpiration } from '../auth/actions';
import AuthUtils from '../shared/utils/auth-utils';
import Modal from '../shared/containers/Modal';
import Walkthrough from '../walkthrough';
import { ProfileContext } from '../shared/contexts/profile.context';
import { selectUserOrganization } from '../store/selectors/store.selectors';
import { RefreshSubscriptionStatus } from '../store/actions/billing.action';
import { selectSyncSubscriptionStatus } from '../store/selectors/billing.selectors';
import { NOT_FIRST_LOGIN_TAG } from '../shared/constants/tags';

interface AppProps extends DispatchProp {
  loadingError: string;
  userLoading: boolean;
  user: UserInfo;
}

function App(props: AppProps) {
  const { dispatch, loadingError, userLoading, user } = props;
  // Then we set the value in the --vh custom property to the root of the document
  document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get('token');
  const org = useSelector(selectUserOrganization);
  const isSubscriptionSynchronized = useSelector(selectSyncSubscriptionStatus);

  // ==========================================================
  //                  Walkthrough logic
  const [showWalkthrough, setShowWalkthrough] = useState(true);
  const [userStartedTour, setUserStartedTour] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  // add and remove classes for onboarding walkthrough
  const [focusOnJobsPage, setFocusOnJobsPage] = useState(false);
  const [focusOnDBPage, setFocusOnDBPage] = useState(false);
  const [focusOnAgentsPage, setFocusOnAgentsPage] = useState(false);
  const [focusOnCustomizePage, setFocusOnCustomizePage] = useState(false);
  const [focusOnProfile, setFocusOnProfile] = useState(false);
  const [focusOnEditProfile, setFocusOnEditProfile] = useState(false);
  const [hideOnboarding, setHideOnboarding] = useState(false);

  const handleElementFocus = (focus: boolean, path: string, id: string) => {
    if (focus && path && id === "edit-profile-modal") {
      // edit profile modal already highlighted, so no need to highlight
      history.replace(path);
    } else if (focus && path) {
      history.replace(path);
      setTimeout(() => {
        const el = document.getElementById(id);
        el?.classList.add("highlight");
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el && el.classList) {
        el.classList.remove("highlight");
      }
    }
  }

  useEffect(() => {
    handleElementFocus(focusOnJobsPage, "/jobs/search", "jobs-page");
    handleElementFocus(focusOnDBPage, "/dashboard", "");
    handleElementFocus(focusOnAgentsPage, "/technicians", "agents-page");
    handleElementFocus(focusOnCustomizePage, "/customize", "customize-page");
    handleElementFocus(focusOnProfile, "/dashboard", "profile-dropdown");
    handleElementFocus(focusOnEditProfile, "/profile", "edit-profile-modal");
  }, [focusOnJobsPage, focusOnAgentsPage, focusOnCustomizePage, focusOnProfile, focusOnEditProfile, focusOnDBPage]);

  const resetWalkThrough = () => {
    setShowWalkthrough(false);
    setUserStartedTour(false);
    // remove highlight class from jobs page
    setFocusOnJobsPage(false);
    setFocusOnDBPage(false);
    setFocusOnAgentsPage(false);
    setFocusOnCustomizePage(false);
    setFocusOnProfile(false);
    setFocusOnEditProfile(false);
  }

  const endWalkthrough = () => {
    resetWalkThrough();
    Cookies.set("showOnBoardingNextTime", "false");
    history.replace("/dashboard");
  }

  const handleNotNow = () => {
    resetWalkThrough();
    Cookies.set("showOnBoardingNextTime", "true");
  }

  const handleHideOnboarding = () => {
    setHideOnboarding(true);
  };
  // ==========================================================

  useEffect(() => {
    if (token) {
      dispatch(new LoginRequest({
        token
      }));
    }
  }, [token]);

  useEffect(() => {
    const subscriptionID = org?.billing_subscription?.current_plans?.subscription_id;
    if (subscriptionID && !isSubscriptionSynchronized) {
      dispatch(new RefreshSubscriptionStatus(subscriptionID, org?._id));
    }
  }, [org?.billing_subscription?.current_plans?.subscription_id]);

  useEffect(() => {
    if (isEmpty(user) && AuthUtils.isAuth()) {
      dispatch(new LoadUserProfileInfo());
    }
  }, [isNil(user)]);

  useEffect(() => {
    if (AuthUtils.isAuth()) {
      dispatch(new WatchExpiration());
      params.delete('token');
      history.replace({
        search: params.toString(),
      })
    }
  }, [AuthUtils.isAuth()]);

  const hasRegisteredWithChargeBee = !isEmpty(org?.billing_subscription);
  useEffect(() => {
    window.Chargebee.registerAgain();
  });

  const profile = useSelector(selectUserProfile);
  const notFirstLogin = profile?.tags?.indexOf(NOT_FIRST_LOGIN_TAG) > -1;
  //

  const userLoadingComplete = !userLoading && (!isEmpty(user) || isEmpty(loadingError));
  const showOnBoardingNextTime = Cookies.get("showOnBoardingNextTime");
  const showOnboarding = AuthUtils.isAuth() && hasRegisteredWithChargeBee && (userStartedTour || (
    (!showOnBoardingNextTime || showOnBoardingNextTime === "true") &&
    (!notFirstLogin || showOnBoardingNextTime === "true") && !hideOnboarding
  ));

  return (
    <>
      { userLoading &&
        <div className="c-app c-default-layout flex-row align-items-center justify-content-center">
          <CSpinner
            className="primary-color"
            style={{width:'3rem', height:'3rem'}}
          />
        </div>
      }
      { userLoadingComplete && !token && (
        <ProfileContext.Provider value={{ showProfileDropdown, showEditProfile, setUserStartedTour, handleHideOnboarding }}>
          <AppRouting />
        </ProfileContext.Provider>
      )}
      <Toaster />
      {showOnboarding &&
        <div className="walkthroughWrapper">
          <Walkthrough
            setAgentPageFocus={setFocusOnAgentsPage}
            setJobsPageFocus={setFocusOnJobsPage}
            setDBPageFocus={setFocusOnDBPage}
            setCustomizePageFocus={setFocusOnCustomizePage}
            handleNotNow={handleNotNow}
            setShowProfileDropdown={setShowProfileDropdown}
            setShowEditProfile={setShowEditProfile}
            userStartedTour={userStartedTour}
            showWalkthrough={showWalkthrough}
            setProfileFocus={setFocusOnProfile}
            setEditProfileFocus={setFocusOnEditProfile}
            endWalkthrough={endWalkthrough}
            user={user}
          />
        </div>
      }
      <Modal />
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  userLoading: state?.profile?.isLoading,
  loadingError: state?.profile?.error,
  user: state?.profile?.userInfo,
})

export default connect(mapStateToProps)(App);
